import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import {
    ColumnDef,
    createColumnHelper,
    getCoreRowModel,
    Row,
    useReactTable,
    type RowSelectionState,
    type OnChangeFn,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { View } from "../../common/types/views/views";
import { ViewConfig } from "../../lib/views";
import { SortableHeader } from "../general/SortableHeader/SortableHeader";
import { Loader } from "../general/Loader";
import { ViewTableSection } from "./ViewTableSection";
import styles from "./ViewTable.module.scss";
import { ViewTableCheckboxHeader } from "./ViewTableCheckboxHeader";
import { ViewTableCheckboxCell } from "./ViewTableCheckboxCell";
// Row height constant for virtualization
const DEFAULT_ROW_HEIGHT = 64;

export const BLOCK_EVENT_KEY = "view-click-block-event";

export interface ViewTableProps<
    TData extends { id: number | string },
    TColumn extends string,
    TSort extends string,
> {
    data: TData[] | null;
    view: View;
    config: ViewConfig<TData, TColumn, TSort>;
    selectedRowId?: TData["id"];
    onRowClick?: (row: TData) => void;
    onRowHover?: (row: TData | null) => void;
    rowHeight?: number;
    className?: string;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    sort?: TSort;
    onSortChange?: (sort: TSort) => void;
    scrollContainerRef?: React.RefObject<HTMLElement>;
    emptyView?: () => React.ReactNode;
    loadingView?: () => React.ReactNode;
}

export function ViewTable<
    TData extends { id: number | string },
    TColumn extends string,
    TSort extends string,
>({
    data,
    view,
    config,
    selectedRowId,
    onRowClick,
    onRowHover,
    rowHeight = DEFAULT_ROW_HEIGHT,
    rowSelection: controlledRowSelection,
    onRowSelectionChange: controlledOnRowSelectionChange,
    sort,
    onSortChange,
    scrollContainerRef,
    emptyView = () => "No data available",
    loadingView = () => <Loader />,
}: Readonly<ViewTableProps<TData, TColumn, TSort>>) {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const centerScrollRef = useRef<HTMLDivElement>(null);
    const hoveredRowIdRef = useRef<number | string | null>(null);

    const [showShadowLeft, setShowShadowLeft] = useState(false);
    const [showShadowRight, setShowShadowRight] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);

    const [uncontrolledRowSelection, setUncontrolledRowSelection] =
        useState<RowSelectionState>({});
    const rowSelection = controlledRowSelection ?? uncontrolledRowSelection;
    const setRowSelection =
        controlledOnRowSelectionChange ?? setUncontrolledRowSelection;

    const columnHelper = useMemo(() => createColumnHelper<TData>(), []);

    const selectionColumn = useMemo<ColumnDef<TData> | null>(() => {
        if (!view.options?.bulkActions) {
            return null;
        }
        return {
            id: "select",
            header: ViewTableCheckboxHeader,
            cell: ViewTableCheckboxCell,
            size: 32,
            meta: { pin: "left" },
        };
    }, [view.options?.bulkActions]);

    const columns = useMemo(() => {
        const baseColumns = view.columns.map((column) => {
            const columnConfig = config.columns[column.id as TColumn];
            const columnDef: ColumnDef<TData> = {
                id: column.id,
                header: columnConfig.sort
                    ? () => (
                          <SortableHeader
                              sort={sort}
                              asc={columnConfig.sort?.asc as TSort}
                              desc={columnConfig.sort?.desc as TSort}
                              onChange={onSortChange}
                              defaultOrder={"asc"}
                          >
                              {column.options?.header ?? columnConfig.name}
                          </SortableHeader>
                      )
                    : column.options?.header ?? columnConfig.name,
                meta: column.options ?? {},
            };
            if (columnConfig.cell) {
                columnDef.cell = columnConfig.cell as ColumnDef<TData>["cell"];
            }
            return columnHelper.accessor(
                columnConfig.accessorKey as any,
                columnDef,
            );
        });
        return selectionColumn
            ? [selectionColumn, ...baseColumns]
            : baseColumns;
    }, [view, config, columnHelper, selectionColumn, sort, onSortChange]);

    const columnPinning = useMemo(() => {
        const left: string[] = [];
        const right: string[] = [];
        if (selectionColumn) {
            left.push("select");
        }
        view.columns.forEach((column) => {
            if (column.options?.pin === "left") {
                left.push(column.id);
            } else if (column.options?.pin === "right") {
                right.push(column.id);
            }
        });
        return { left, right };
    }, [view.columns, selectionColumn]);

    const handleRowHover = useCallback(
        (rowId: number | string | null) => {
            if (!data) {
                return;
            }
            if (!view.options?.selection) {
                return;
            }
            if (!tableContainerRef.current) {
                return;
            }
            if (hoveredRowIdRef.current === rowId) {
                return;
            }
            // Clear previous hover state
            if (hoveredRowIdRef.current !== null) {
                const previousRows = tableContainerRef.current.querySelectorAll(
                    `tr[data-row-id="${hoveredRowIdRef.current}"]`,
                );
                previousRows.forEach((row) => {
                    row.classList.remove(styles.hoveredRow);
                });
            }
            // Set new hover state
            if (rowId !== null) {
                const newRows = tableContainerRef.current.querySelectorAll(
                    `tr[data-row-id="${rowId}"]`,
                );
                newRows.forEach((row) => {
                    row.classList.add(styles.hoveredRow);
                });
            }
            hoveredRowIdRef.current = rowId;
            if (onRowHover) {
                const row = data?.find((d) => d.id === rowId) ?? null;
                onRowHover(row);
            }
        },
        [data, onRowHover, view.options?.selection],
    );

    const table = useReactTable<TData>({
        data: data ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: { columnPinning, rowSelection },
        enableColumnPinning: true,
        enableRowSelection: !!view.options?.bulkActions,
        onRowSelectionChange: setRowSelection,
        meta: {
            setHoveredRowId: handleRowHover,
            centerScrollRef,
            options: view.options,
        },
        getRowId: (row: any) => row.id,
    });

    const handleRowClick = (e: any, row: TData) => {
        if (e[BLOCK_EVENT_KEY]) {
            return;
        }
        if (onRowClick) {
            onRowClick(row);
        }
    };
    const isRowActive = (row: TData) =>
        selectedRowId !== undefined && row.id === selectedRowId;

    // Row props factory
    const getRowProps = (row: Row<TData>) => ({
        className: classNames(styles.clickableRow, {
            [styles.activeRow]: isRowActive(row.original),
        }),
        onClick: (e: any) => handleRowClick(e, row.original),
        onMouseEnter: () => handleRowHover(row.original.id),
        onMouseLeave: () => handleRowHover(null),
        "data-row-id": row.original.id,
        "data-index": row.index,
    });

    const rowVirtualizer = useVirtualizer({
        count: data?.length ?? 0,
        getScrollElement: () =>
            scrollContainerRef?.current ??
            document.scrollingElement ??
            document.documentElement,
        estimateSize: () => rowHeight,
        overscan: 25,
        scrollMargin: rowHeight * 2,
    });

    useEffect(() => {
        const container = scrollContainerRef?.current ?? window;
        const updateVirtualizerOffset = () => {
            if (scrollContainerRef?.current) {
                const rect = scrollContainerRef.current.getBoundingClientRect();
                const newOffset = Math.max(0, -rect.top);
                rowVirtualizer.scrollOffset = newOffset;
                rowVirtualizer.measure();
            } else if (tableContainerRef.current) {
                const rect = tableContainerRef.current.getBoundingClientRect();
                const newOffset = Math.max(0, -rect.top);
                rowVirtualizer.scrollOffset = newOffset;
                rowVirtualizer.measure();
            }
        };
        container.addEventListener("scroll", updateVirtualizerOffset);
        window.addEventListener("resize", updateVirtualizerOffset);
        updateVirtualizerOffset();
        return () => {
            container.removeEventListener("scroll", updateVirtualizerOffset);
            window.removeEventListener("resize", updateVirtualizerOffset);
        };
    }, [rowVirtualizer, scrollContainerRef]);

    // Handle shadows for horizontal scrolling
    useEffect(() => {
        const handleScroll = () => {
            const el = centerScrollRef.current;
            if (!el) {
                return;
            }
            setShowShadowLeft(el.scrollLeft > 0);
            setShowShadowRight(
                el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
            );
            setHasOverflow(el.scrollWidth > el.clientWidth);
        };
        const currentCenterScrollRef = centerScrollRef.current;
        if (currentCenterScrollRef) {
            currentCenterScrollRef.addEventListener("scroll", handleScroll);
            window.addEventListener("resize", handleScroll);
            handleScroll();
        }
        return () => {
            if (currentCenterScrollRef) {
                currentCenterScrollRef.removeEventListener(
                    "scroll",
                    handleScroll,
                );
            }
            window.removeEventListener("resize", handleScroll);
        };
    }, [selectedRowId]);

    // Virtualization padding
    const virtualRows = rowVirtualizer.getVirtualItems();
    const paddingTop =
        virtualRows.length > 0 && virtualRows[0].index > 0
            ? virtualRows[0].start
            : 0;
    const paddingBottom =
        virtualRows.length > 0
            ? rowVirtualizer.getTotalSize() -
              virtualRows[virtualRows.length - 1].end
            : 0;

    const isEmpty = table.getRowModel().rows.length === 0;
    const isLoading = data === null;

    return (
        <>
            <div
                className={classNames(styles.tableContainer, {
                    [styles.showLeftShadow]: showShadowLeft,
                    [styles.showRightShadow]: showShadowRight,
                    [styles.hasOverflow]: hasOverflow,
                })}
                ref={tableContainerRef}
            >
                <ViewTableSection
                    sectionType="left"
                    table={table}
                    paddingTop={paddingTop}
                    paddingBottom={paddingBottom}
                    virtualRows={virtualRows}
                    getRowProps={getRowProps}
                />
                <ViewTableSection
                    sectionType="center"
                    table={table}
                    paddingTop={paddingTop}
                    paddingBottom={paddingBottom}
                    virtualRows={virtualRows}
                    getRowProps={getRowProps}
                />
                <ViewTableSection
                    sectionType="right"
                    table={table}
                    paddingTop={paddingTop}
                    paddingBottom={paddingBottom}
                    virtualRows={virtualRows}
                    getRowProps={getRowProps}
                />
            </div>
            {(isEmpty || isLoading) && (
                <div className={styles.blankView}>
                    {isLoading ? loadingView() : emptyView()}
                </div>
            )}
        </>
    );
}
